import React, { useState, useEffect, useRef } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

// Support different prop types for flexibility
interface TimeProps {
  // Original API for backward compatibility
  timestamp?: number;
  // New API to support Date objects directly
  date?: Date;
  className?: string;
  fallback?: string;
  showTooltip?: boolean;
}

export function RelativeTime({ 
  timestamp, 
  date, 
  className = "text-xs text-muted-foreground/60",
  fallback = "Never",
  showTooltip = true
}: TimeProps) {
  // Store the original timestamp value to prevent it changing during re-renders
  const initialTimeRef = useRef<number | null>(null);
  
  // Initialize the stored timestamp only once
  if (initialTimeRef.current === null) {
    initialTimeRef.current = timestamp || (date?.getTime() || null);
  }
  
  // Use the stored original time
  const time = initialTimeRef.current;
  
  // Debugging
  // console.log("RelativeTime rendering:", { providedTimestamp: timestamp, providedDate: date?.toString(), usingTime: time });
  
  // Safely format the relative time
  const formatRelativeTime = (timeValue: number): string => {
    try {
      return formatDistanceToNow(new Date(timeValue), { addSuffix: true });
    } catch (e) {
      console.error("Error formatting relative time:", e);
      return "Unknown time";
    }
  };
  
  // Hooks must be called before any conditional returns
  const [relativeTime, setRelativeTime] = useState(() => 
    time ? formatRelativeTime(time) : fallback
  );

  useEffect(() => {
    // Define the stored time inside the effect to ensure it doesn't change
    const effectTime = initialTimeRef.current; 
    if (!effectTime) return;
    
    // Update immediately
    setRelativeTime(formatRelativeTime(effectTime));

    // Determine update interval based on how old the timestamp is
    const age = Date.now() - effectTime;
    let interval = 60000; // Default to 1 minute

    if (age < 60000) { // Less than 1 minute old
      interval = 10000; // Update every 10 seconds
    } else if (age < 3600000) { // Less than 1 hour old
      interval = 60000; // Update every minute
    } else if (age < 86400000) { // Less than 1 day old
      interval = 300000; // Update every 5 minutes
    } else {
      interval = 3600000; // Update every hour for older messages
    }

    // Set up interval for updates
    const timer = setInterval(() => {
      setRelativeTime(formatRelativeTime(effectTime));
    }, interval);

    return () => clearInterval(timer);
  }, []); // Important: empty dependency array means this only runs once

  // Handle missing or invalid timestamps
  if (!time) {
    return <span className={className}>{fallback}</span>;
  }
  
  // Simple version without tooltip
  if (!showTooltip) {
    return <span className={className}>{relativeTime}</span>;
  }

  // With tooltip showing exact timestamp
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className={className}>{relativeTime}</span>
        </TooltipTrigger>
        <TooltipContent>
          {new Date(time).toLocaleString()}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

// For backward compatibility
export default RelativeTime;